<template>
  <section class="extrato-print-document">
    <h1>{{ titulo }}</h1>
    <p v-if="!dados" role="alert">Impressão não preparada. Feche esta prévia, use o botão Imprimir no extrato e aguarde a conclusão. Nenhuma movimentação foi incluída.</p>
    <template v-else>
      <p>Período: {{ data(dados.periodo.from) }} a {{ data(dados.periodo.to) }} · {{ dados.itens.length }} movimentos</p>
      <table aria-label="Resumo financeiro">
        <tbody><tr v-for="row in resumo" :key="row[0]"><th>{{ row[0] }}</th><td class="money">{{ moeda(row[1]) }}</td></tr></tbody>
      </table>
      <template v-if="referencias.length">
        <h2>Referências financeiras e diferenças de referência</h2>
        <p>Saldos de encerramento. As diferenças não são receitas ou despesas.</p>
        <table aria-label="Referências financeiras">
          <thead><tr><th>Data</th><th>Referência</th><th>Calculado antes</th><th>Declarado</th><th>Diferença</th></tr></thead>
          <tbody><tr v-for="row in referencias" :key="row[1]"><td>{{ data(row[0]) }}</td><td>{{ row[1] }}</td><td v-for="(valor, i) in row.slice(2)" :key="i" class="money">{{ moeda(valor) }}</td></tr></tbody>
        </table>
      </template>
      <h2>Movimentos no período</h2>
      <table aria-label="Movimentos no período">
        <colgroup><col style="width:11%"><col style="width:17%"><col style="width:13%"><col style="width:34%"><col style="width:12%"><col style="width:13%"></colgroup>
        <thead><tr><th>Data</th><th>Tipo</th><th>Valor</th><th>Origem</th><th>Autorização</th><th>Saldo do dia</th></tr></thead>
        <tbody><tr v-for="(item, i) in dados.itens" :key="i" data-extrato-movimento>
          <td>{{ data(item.dt_caixa) }}</td><td>{{ tipo(item) }}</td><td class="money">{{ moeda(item.valor) }}</td>
          <td>{{ item.origem_label || item.origem_tabela || '—' }}</td><td>{{ item.numero_autorizacao || '—' }}</td>
          <td class="money">{{ primeiroDia(dados.itens, i) ? moeda(saldos[chave(item.dt_caixa)]) : '' }}</td>
        </tr></tbody>
      </table>
      <template v-if="incluirFuturos && dados.futuros && dados.futuros.length">
        <h2>Projeção dos lançamentos exibidos (sem referências futuras)</h2>
        <table aria-label="Lançamentos futuros">
          <thead><tr><th>Data</th><th>Tipo</th><th>Valor</th><th>Origem</th><th>Saldo previsto</th></tr></thead>
          <tbody><tr v-for="(item, i) in dados.futuros" :key="i"><td>{{ data(item.dt_caixa) }}</td><td>{{ tipo(item) }}</td><td class="money">{{ moeda(item.valor) }}</td><td>{{ item.origem_label || item.origem_tabela || '—' }}</td><td class="money">{{ primeiroDia(dados.futuros, i) ? moeda(saldosFuturos[chave(item.dt_caixa)]) : '' }}</td></tr></tbody>
        </table>
      </template>
      <footer>Fim do extrato · {{ dados.itens.length }} movimentos no período · {{ titulo }}</footer>
    </template>
  </section>
</template>
<script>
import { computed } from 'vue';
import { linhasResumo, linhasReferencias, mapaSaldos } from '@/utils/extratoPresentation.mjs';
export default {
  props: { dados: { type: Object, default: null }, titulo: { type: String, required: true }, incluirFuturos: Boolean },
  setup(props) {
    const chave = value => String(value || '').slice(0, 10);
    const data = value => chave(value).split('-').reverse().join('/') || '—';
    const moeda = value => value == null ? 'Desconhecido' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
    const primeiroDia = (rows, i) => i === 0 || chave(rows[i - 1].dt_caixa) !== chave(rows[i].dt_caixa);
    const tipo = item => item.forma_pagamento_nome && item.forma_pagamento_nome.trim().toLowerCase() !== 'a informar' ? `${item.tipo} — ${item.forma_pagamento_nome}` : item.tipo;
    return { chave, data, moeda, primeiroDia, tipo,
      resumo: computed(() => linhasResumo(props.dados?.resumo)),
      referencias: computed(() => linhasReferencias(props.dados?.referencias_periodo)),
      saldos: computed(() => mapaSaldos(props.dados?.saldos_diarios)),
      saldosFuturos: computed(() => mapaSaldos(props.dados?.futuros_saldos_diarios)) };
  }
};
</script>
<style>
/* Only the mounted modal owns this body-level print root. Screen layout is untouched. */
.extrato-print-root { display: none; }
@page extrato-conta { size: A4 portrait; margin: 12mm; }
@media print {
  html:has(.extrato-print-root), body:has(> .extrato-print-root) {
    display: block !important; position: static !important; height: auto !important;
    max-height: none !important; overflow: visible !important; margin: 0 !important; padding: 0 !important;
  }
  body:has(> .extrato-print-root) > :not(.extrato-print-root) { display: none !important; }
  body > .extrato-print-root {
    display: block !important; position: static !important; width: auto !important;
    height: auto !important; max-height: none !important; overflow: visible !important;
    margin: 0 !important; padding: 0 !important; page: extrato-conta;
  }
  .extrato-print-document { color: #000; background: #fff; font: 9pt Arial, sans-serif; }
  .extrato-print-document h1 { font-size: 14pt; margin: 0 0 4mm; }
  .extrato-print-document h2 { font-size: 11pt; margin: 5mm 0 2mm; break-after: avoid; }
  .extrato-print-document table { width: 100%; table-layout: fixed; border-collapse: collapse; margin-bottom: 4mm; }
  .extrato-print-document th, .extrato-print-document td { padding: 1.5mm; border: .2mm solid #bbb; vertical-align: top; white-space: normal; overflow-wrap: anywhere; }
  .extrato-print-document thead { display: table-header-group; }
  .extrato-print-document tr { break-inside: avoid; }
  .extrato-print-document .money { text-align: right; font-variant-numeric: tabular-nums; }
  .extrato-print-document footer { margin-top: 4mm; font-size: 8pt; }
}
</style>
